/* eslint-disable camelcase */

import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import FormSelect from '../../ui/Select/FormSelect'
import Button from '../../ui/Button'
import RadioGroup from '../../ui/RadioGroup'
import AddLink from './AddLink'
import RemoveButton from './RemoveButton'
import ConditionValueField from './ConditionValueField'
import ScopedSelect from './ScopedSelect'
import {
  KIND_OPTIONS, emptyCondition, emptyAction, isPersisted, stateOptionsFor, valueInputKind,
  sourceGroups, variableSourceOptions, sourceValue, sourceContext, targetQuestionOptions, valueOptions,
  variableAnswerOptions, splitValue, ruleSectionId, questionSectionId,
  excludeSourceQuestions, excludeTargetQuestions, excludeQuestionsBeforeSources, selectValue,
  flattenGroupOptions, sourceDatetimeFormat
} from './helpers'
import { GUTTER, ACTIONS_BOX, FRAME, FRAME_HEADER, FRAME_BODY } from './styles'

const ICON_BTN = [
  'tw:inline-flex',
  'tw:items-center',
  'tw:justify-center',
  'tw:cursor-pointer',
  'tw:rounded-md!',
  'tw:w-9',
  'tw:h-9',
  'tw:leading-none'
].join(' ')
const CANCEL_BTN = `${ICON_BTN} tw:border tw:border-solid tw:border-gray-300 tw:bg-white tw:text-gray-600`
const VALIDATE_BTN = `${ICON_BTN} tw:border-0 tw:bg-ds-primary tw:text-white`
  + ' tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'

const questionIdSet = records =>
  new Set(records
    .filter(record => !record.shouldDestroy && record.targetable_type === 'Question' && record.targetable_id)
    .map(record => String(record.targetable_id)))

const firstConditionSectionId = (isExistingRule, masterSectionId, initialSectionId) => {
  if (isExistingRule)
    return masterSectionId
  if (initialSectionId)
    return initialSectionId
  return masterSectionId ? null : initialSectionId
}

const conditionSources = (isVariableSource, variables, { sections, scopeId, excludedIds }) => {
  if (isVariableSource)
    return variableSourceOptions(variables)
  return excludeSourceQuestions(sourceGroups(sections, scopeId), excludedIds)
}

const actionSectionId = (isExistingRule, masterSectionId, opts) => {
  const { sections, allRecords, actionUniqueId, initialSectionId } = opts
  if (isExistingRule)
    return masterSectionId
  if (initialSectionId)
    return initialSectionId
  return ruleSectionId(sections, allRecords, actionUniqueId)
}

const dropIfOtherSection = (records, { sections, sectionId, keepUniqueId, clearValue }) =>
  records.map(record => {
    if (record.uniqueId === keepUniqueId || record.targetable_type !== 'Question')
      return record
    if (questionSectionId(sections, record.targetable_id) === sectionId)
      return record
    return clearValue ? { ...record, targetable_id: null, value: null } : { ...record, targetable_id: null }
  })

const flagPersisted = (record, uniqueId) =>
  record.uniqueId === uniqueId && isPersisted(record) ? { ...record, shouldDestroy: true } : record

const dropOrFlag = (records, uniqueId) =>
  records
    .map(record => flagPersisted(record, uniqueId))
    .filter(record => !(record.uniqueId === uniqueId && !isPersisted(record)))

const ConditionForm = ({
  initialSectionId, number, rule, sections, variables, gradeConfig, inline, onSubmit, onCancel
}) => {
  const [conditions, setConditions] = useState(rule.conditions)
  const [actions, setActions] = useState(rule.rule_actions)

  const visibleConditions = conditions.filter(condition => !condition.shouldDestroy)
  const visibleActions = actions.filter(action => !action.shouldDestroy)

  const allRecords = [...conditions, ...actions]
  const masterSectionId = ruleSectionId(sections, allRecords, null)
  const conditionQuestionIds = questionIdSet(conditions)
  const actionQuestionIds = questionIdSet(actions)
  const isExistingRule = isPersisted(rule)
  const firstSectionId = firstConditionSectionId(isExistingRule, masterSectionId, initialSectionId)
  const hasVariables = variableSourceOptions(variables).length > 0

  const contextOf = condition => sourceContext(sections, variables, condition)
  const datetimeFormatOf = condition => sourceDatetimeFormat(sections, condition)

  const sourceTypeOptions = [
    { value: 'Question', label: 'Par réponse' },
    { value: 'Variable', label: 'Par variable', disabled: !hasVariables }
  ]

  const updateCondition = (uniqueId, changes) =>
    setConditions(prev => prev.map(condition =>
      condition.uniqueId === uniqueId ? { ...condition, ...changes } : condition))

  const updateAction = (uniqueId, changes) =>
    setActions(prev => prev.map(action =>
      action.uniqueId === uniqueId ? { ...action, ...changes } : action))

  const removeCondition = uniqueId => () => setConditions(prev => dropOrFlag(prev, uniqueId))
  const removeAction = uniqueId => () => setActions(prev => dropOrFlag(prev, uniqueId))

  const addCondition = useCallback(() => setConditions(prev => [...prev, emptyCondition()]), [])
  const addAction = useCallback(() => setActions(prev => [...prev, emptyAction()]), [])

  const rescopeTo = (sectionId, keepUniqueId) => {
    if (!sectionId)
      return
    setConditions(prev => dropIfOtherSection(prev, { sections, sectionId, keepUniqueId, clearValue: true }))
    setActions(prev => dropIfOtherSection(prev, { sections, sectionId, keepUniqueId, clearValue: false }))
  }

  const handleTypeChange = condition => nextType => {
    if (nextType === condition.targetable_type)
      return
    updateCondition(condition.uniqueId, {
      targetable_type: nextType,
      targetable_id: null,
      state: nextType === 'Variable' ? stateOptionsFor('variable')[0].value : 'in',
      value: null
    })
  }

  const handleSourceChange = condition => option => {
    const context = option ? option.context : null
    const state = context ? stateOptionsFor(context)[0].value : 'in'
    updateCondition(condition.uniqueId, {
      targetable_type: option ? option.type : condition.targetable_type,
      targetable_id: option ? option.sourceId : null,
      state,
      value: null
    })
    if (option && option.type === 'Question')
      rescopeTo(questionSectionId(sections, option.sourceId), condition.uniqueId)
  }

  const handleStateChange = condition => option => {
    const cleared = option.value === 'filled' || option.value === 'empty'
    updateCondition(condition.uniqueId, { state: option.value, value: cleared ? null : condition.value })
  }

  const handleValueChange = condition => value => updateCondition(condition.uniqueId, { value })

  const handleKindChange = action => option => updateAction(action.uniqueId, { kind: option.value })

  const handleTargetChange = action => option => {
    updateAction(action.uniqueId, { targetable_id: option ? option.value : null })
    if (option)
      rescopeTo(questionSectionId(sections, option.value), action.uniqueId)
  }

  const conditionValid = condition => {
    if (!condition.targetable_id)
      return false
    const kind = valueInputKind(contextOf(condition), condition.state, datetimeFormatOf(condition))
    if (kind === 'none')
      return true
    if (kind === 'option' || kind === 'option-multi' || kind === 'tags')
      return splitValue(condition.value).length > 0
    return Boolean(condition.value && String(condition.value).trim())
  }

  const isValid = visibleConditions.length > 0 && visibleActions.length > 0
    && visibleConditions.every(conditionValid)
    && visibleActions.every(action => action.targetable_id)

  const handleSubmit = useCallback(() => {
    if (isValid)
      onSubmit({ ...rule, conditions, rule_actions: actions })
  }, [isValid, onSubmit, rule, conditions, actions])

  const fields = (
    <div className="tw:flex tw:flex-1 tw:min-w-0 tw:flex-col tw:gap-4">
      {visibleConditions.map((condition, index) => {
        const context = contextOf(condition)
        const isVariableSource = condition.targetable_type === 'Variable'
        const sourceCtx = isVariableSource ? 'selection' : context
        const kind = valueInputKind(sourceCtx, condition.state, datetimeFormatOf(condition))
        let editorOptions = valueOptions(sections, condition, gradeConfig)
        if (isVariableSource)
          editorOptions = variableAnswerOptions(variables, condition.targetable_id)
        const stateOptions = stateOptionsFor(context)
        const scopeId = index === 0 ? firstSectionId : masterSectionId || firstSectionId
        const sources = conditionSources(
          isVariableSource, variables, { sections, scopeId, excludedIds: actionQuestionIds }
        )
        const removeThisCondition = removeCondition(condition.uniqueId)
        return (
          <div key={condition.uniqueId} className="tw:flex tw:flex-col tw:gap-2">
            <RadioGroup
              className="tw:m-0!"
              name={`source_type_${condition.uniqueId}`}
              options={sourceTypeOptions}
              value={condition.targetable_type}
              inline
              onChange={handleTypeChange(condition)}
            />
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:shrink-0 tw:font-bold tw:text-gray-900">
                {index === 0 ? 'SI' : 'ET'}
              </span>
              <div className="tw:flex-1 tw:min-w-0">
                <ScopedSelect
                  options={sources}
                  placeholder={isVariableSource ? 'Choisir une variable' : 'Choisir une question'}
                  value={sourceValue(sources, condition.targetable_type, condition.targetable_id)}
                  menuPortal
                  onChange={handleSourceChange(condition)}
                />
              </div>
              {visibleConditions.length > 1 && <RemoveButton onClick={removeThisCondition} />}
            </div>
            <div className="tw:flex tw:items-start tw:gap-2">
              <div className="tw:shrink-0 tw:w-[150px]!">
                <FormSelect
                  isSearchable={false}
                  isDisabled={!condition.targetable_id}
                  options={stateOptions}
                  value={selectValue(stateOptions, condition.state)}
                  menuPortal
                  onChange={handleStateChange(condition)}
                />
              </div>
              {Boolean(condition.targetable_id) && kind !== 'none' && (
                <div className="tw:flex-1">
                  <ConditionValueField
                    condition={condition}
                    kind={kind}
                    options={editorOptions}
                    onValueChange={handleValueChange(condition)}
                  />
                </div>
              )}
            </div>
          </div>
        )
      })}
      <AddLink label="Ajouter une règle" onClick={addCondition} />
      <div className={ACTIONS_BOX}>
        {visibleActions.map(action => {
          const targetSectionId = actionSectionId(isExistingRule, masterSectionId, {
            sections, allRecords, actionUniqueId: action.uniqueId, initialSectionId
          })
          const scopedTargets = targetQuestionOptions(sections, targetSectionId)
          const takenTargets = questionIdSet(actions.filter(other => other.uniqueId !== action.uniqueId))
          const excludedTargets = new Set([...conditionQuestionIds, ...takenTargets])
          const orderedTargets = excludeQuestionsBeforeSources(
            scopedTargets, sections, conditionQuestionIds
          )
          const targetGroups = excludeTargetQuestions(orderedTargets, excludedTargets)
          return (
            <div key={action.uniqueId} className="tw:flex tw:items-center tw:gap-2">
              <div className="tw:shrink-0 tw:w-[150px]!">
                <FormSelect
                  isSearchable={false}
                  options={KIND_OPTIONS}
                  value={selectValue(KIND_OPTIONS, action.kind)}
                  menuPortal
                  onChange={handleKindChange(action)}
                />
              </div>
              <div className="tw:flex-1 tw:min-w-0">
                <ScopedSelect
                  options={targetGroups}
                  placeholder="Choisir une question"
                  value={selectValue(flattenGroupOptions(targetGroups), action.targetable_id)}
                  menuPortal
                  onChange={handleTargetChange(action)}
                />
              </div>
              {visibleActions.length > 1 && <RemoveButton onClick={removeAction(action.uniqueId)} />}
            </div>
          )
        })}
        <AddLink label="Ajouter" onClick={addAction} />
      </div>
    </div>
  )

  if (!isExistingRule && !inline) {
    return (
      <div className={FRAME}>
        <div className={FRAME_HEADER}>
          <span className="tw:flex-1 tw:font-normal tw:text-gray-900">Nouvelle condition</span>
          <button aria-label="Annuler" className={CANCEL_BTN} type="button" onClick={onCancel}>
            <i className="fas fa-times" />
          </button>
          <button
            aria-label="Valider"
            className={VALIDATE_BTN}
            disabled={!isValid}
            type="button"
            onClick={handleSubmit}
          >
            <i className="fas fa-check" />
          </button>
        </div>
        <div className={FRAME_BODY}>
          {fields}
        </div>
      </div>
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:w-full">
      <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
        <span className={GUTTER}>{number ? `#${number}` : ''}</span>
        {fields}
      </div>
      <div className="tw:flex tw:justify-end tw:gap-2">
        <Button variant="neutral" onClick={onCancel}>Annuler</Button>
        <Button variant="primary" disabled={!isValid} onClick={handleSubmit}>Sauvegarder</Button>
      </div>
    </div>
  )
}

ConditionForm.propTypes = {
  gradeConfig: PropTypes.shape({ gradeMin: PropTypes.number, gradeSize: PropTypes.number }),
  initialSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inline: PropTypes.bool,
  number: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  rule: PropTypes.shape({ uniqueId: PropTypes.string }).isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  variables: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
}

ConditionForm.defaultProps = {
  gradeConfig: null,
  initialSectionId: null,
  inline: false,
  number: null,
  variables: []
}

export default ConditionForm
